import type { Category, ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";

export interface CategoryRecord extends Category {
  createdAt: string;
  updatedAt: string;
}

const LS_KEY = "inventory.categories.v2";

type CategoryApiRecord = {
  id: number | string;
  name: string;
  parent_id?: number | string | null;
  description?: string | null;
  sort_order?: number;
  created_at?: string;
  updated_at?: string;
};

type ListCategoriesApiResponse = {
  data?: CategoryApiRecord[];
  meta?: {
    page?: number;
    per_page?: number;
    total?: number;
  };
};

type UpsertCategoryApiResponse = {
  message?: string;
  data?: CategoryApiRecord;
};

type DeleteCategoryApiResponse = {
  message?: string;
};

type UpsertCategoryResult = {
  record: CategoryRecord;
  message: string;
};

type DeleteCategoryResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: CategoryRecord[]): CategoryRecord[] {
  const byId = new Map<ID, CategoryRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function load(): CategoryRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CategoryRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: CategoryRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

export function listCategoriesRecords(): CategoryRecord[] {
  return load().sort((a, b) => a.name.localeCompare(b.name));
}

export function getCategoryById(id: ID): CategoryRecord | undefined {
  return load().find((c) => c.id === id);
}

function mapApiRecord(record: CategoryApiRecord): CategoryRecord {
  return {
    id: String(record.id),
    name: record.name,
    parentId: record.parent_id == null ? null : String(record.parent_id),
    createdAt: record.created_at || new Date().toISOString(),
    updatedAt: record.updated_at || record.created_at || new Date().toISOString(),
  };
}

export async function syncCategoriesFromApi() {
  const response = await api.get<ListCategoriesApiResponse>("/api/v1/catalog/categories", {
    params: { page: 1, per_page: 200 },
  });
  const records = (response.data?.data ?? []).map(mapApiRecord);
  save(records);
  return listCategoriesRecords();
}

export async function createCategory(data: { name: string; parentId?: ID | null }): Promise<UpsertCategoryResult> {
  const items = load();
  const payload = {
    name: data.name.trim(),
    parent_id: data.parentId ?? null,
  };
  const response = await api.post<UpsertCategoryApiResponse>("/api/v1/catalog/categories", payload);
  const created = response.data?.data;
  if (!created) throw new Error("Invalid create category response.");
  const record = mapApiRecord(created);
  const existingIndex = items.findIndex((i) => i.id === record.id);
  if (existingIndex >= 0) items[existingIndex] = record;
  else items.push(record);
  save(items);
  return { record, message: response.data?.message || "Category created successfully." };
}

export async function updateCategory(
  id: ID,
  data: { name: string; parentId?: ID | null },
): Promise<UpsertCategoryResult | null> {
  const items = load();
  const i = items.findIndex((c) => c.id === id);
  if (i === -1) return null;
  const payload = {
    name: data.name.trim(),
    parent_id: data.parentId ?? null,
  };
  const response = await api.put<UpsertCategoryApiResponse>(`/api/v1/catalog/categories/${id}`, payload);
  const updated = response.data?.data;
  if (!updated) throw new Error("Invalid update category response.");
  items[i] = mapApiRecord(updated);
  save(items);
  return { record: items[i], message: response.data?.message || "Category updated successfully." };
}

export async function deleteCategory(id: ID): Promise<DeleteCategoryResult> {
  const response = await api.delete<DeleteCategoryApiResponse>(`/api/v1/catalog/categories/${id}`);
  const items = load();
  const next = items.filter((c) => c.id !== id);
  const deleted = next.length !== items.length;
  if (deleted) save(next);
  return { success: deleted, message: response.data?.message || "Category deleted successfully." };
}

export async function copyCategory(id: ID): Promise<UpsertCategoryResult> {
  const items = load();
  const response = await api.post<UpsertCategoryApiResponse>(`/api/v1/catalog/categories/${id}/copy`);
  const created = response.data?.data;
  if (!created) throw new Error("Invalid copy category response.");
  const record = mapApiRecord(created);
  const existingIndex = items.findIndex((i) => i.id === record.id);
  if (existingIndex >= 0) items[existingIndex] = record;
  else items.push(record);
  save(items);
  return { record, message: response.data?.message || "Category copied successfully." };
}

export function replaceAllCategories(records: CategoryRecord[]) {
  save(records);
}
